


































































































import { Component, Mixins, Prop } from "vue-property-decorator";
import HasValidation from "@/mixins/HasValidation";
import { DataModules } from "@/store/modules/data";
import { IContract, IContractProduct } from "@/models/contracts";
import { IInvoice } from "@/models/invoices";
import { BACKEND_DATE_FORMAT } from "@/data/date";
import i18n from "@/i18n";
import _ from "@/boot/lodash";

type ReactivateParams = {
  dryRun?: boolean;
  proRataAmount?: number;
  issueRecurring?: boolean;
  nextDueDate?: Date | null;
};

enum CHARGE_OPTIONS {
  PRORATA = "prorata",
  NIL = "nil"
}

@Component({
  components: {
    "editable-price": () =>
      import(
        "@/components/app/global/baskets/basket/productView/editablePrice.vue"
      )
  }
})
export default class CProdReactivationModal extends Mixins(HasValidation) {
  // Props

  @Prop({ required: true })
  public contractId!: IContract["id"];

  @Prop({ required: true })
  public contractProductId!: IContractProduct["id"];

  @Prop({ default: true })
  public openInvoicePreviewModal!: true;

  @Prop({ default: () => i18n.t("_.reactivate") })
  public title!: string;

  // Data

  public hasRequestError = false;
  public invoice: IInvoice | null = null;
  public isLoading = false;
  public isProcessing = false;
  public isReloading = false;
  public issueRecurring = false;
  public nextDueDate = new Date();
  public proRataAmount = 0;
  public proRataAmountFormatted = "";
  public selectedChargeOptionId = CHARGE_OPTIONS.PRORATA;

  // Computed

  public get selectedChargeOption() {
    return _.find(this.chargeOptions, ["id", this.selectedChargeOptionId]);
  }

  public get chargeOptions() {
    return [
      {
        id: CHARGE_OPTIONS.PRORATA,
        label: this.$t("_sentence.cprod.on_reactivate_apply_prorata_charge", {
          amount: this.$store.getters["utils/money/trimTrailingZeros"](
            this.proRataAmountFormatted
          )
        }),
        payload: {
          proRataAmount: this.proRataAmount
        }
      },
      {
        id: CHARGE_OPTIONS.NIL,
        label: this.$t("_sentence.cprod.on_reactivate_apply_zero_charge"),
        amount: undefined,
        payload: {
          proRataAmount: 0
        }
      }
    ];
  }

  // Lifecycle methods

  public async created() {
    try {
      this.isLoading = true;
      await this.doDryRun();
      this.resetForm();
    } finally {
      this.isLoading = false;
    }
  }

  // Methods

  public async doDryRun(params = {}) {
    try {
      this.isReloading = true;
      await this.reactivate({ dryRun: true, ...params });
      this.isReloading = false;
    } catch (error) {
      if (this.$store.getters["api/is409"](error)) {
        this.$store.dispatch("api/handleErrorSnackbar", error);
        return this.$emit("close");
      } else {
        this.$store.dispatch("api/handleError", error);
        this.hasRequestError = true;
      }
    }
  }

  public selectNextDueDate() {
    const nextDueMoment = this.$moment(this.nextDueDate);
    this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () =>
          import("@/components/app/global/date/selectDateModal.vue"),
        parent: this,
        canCancel: ["button"],
        props: {
          title: this.$t("_action.change_next_renewal_date"),
          defaultDate: nextDueMoment.toDate(),
          minDate: this.$moment()
            .startOf("day")
            .toDate(),
          onSelect: async date => {
            this.nextDueDate = this.$moment(date).toDate();
            await this.doDryRun({
              nextDueDate: this.nextDueDate
            });
            this.setProRataAmount(
              _.get(this.invoice, "total_amount_converted")
            );
          }
        },
        width: 440
      }
    });
  }

  public resetForm() {
    this.selectedChargeOptionId = CHARGE_OPTIONS.PRORATA;
    this.nextDueDate = new Date();
    this.proRataAmount = _.get(this.invoice, "total_amount_converted", 0);
    this.proRataAmountFormatted = _.get(
      this.invoice,
      "total_amount_formatted",
      ""
    );
  }

  public async setProRataAmount(amount) {
    if (this.$_.isNil(amount)) return;
    this.proRataAmount = amount;
    this.proRataAmountFormatted = await this.$store.dispatch("api/calculate", {
      prices: [amount],
      currency_id: _.get(this.invoice, "currency_id"),
      returnOnly: "total_formatted"
    });
  }

  public async reactivate({
    proRataAmount,
    dryRun = false,
    issueRecurring = this.issueRecurring,
    nextDueDate = null
  }: ReactivateParams) {
    this.invoice = await this.$store.dispatch(
      `data/${DataModules.CONTRACTS_PRODUCTS}/reactivateProduct`,
      {
        contractId: this.contractId,
        contractProductId: this.contractProductId,
        data: {
          dry_run: dryRun,
          issue_recurring: issueRecurring,
          ...(!_.isNil(proRataAmount)
            ? { pro_rata_amount: proRataAmount }
            : {}),
          ...(nextDueDate
            ? {
                next_due_date: this.$moment(nextDueDate).format(
                  BACKEND_DATE_FORMAT
                )
              }
            : {})
        }
      }
    );
    this.hasRequestError = false;
  }

  public async validateBeforeSubmit() {
    if (this.isSubmitDisabled) return;
    this.isProcessing = true;
    try {
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");
      await this.reactivate({
        dryRun: false,
        proRataAmount: this.selectedChargeOption?.payload.proRataAmount,
        issueRecurring: this.issueRecurring,
        nextDueDate: this.nextDueDate
      });
      this.$emit("close");
      this.$emit("contract-product-updated");
      this.$store.dispatch("toast/show", {
        message: this.$t("_.successfully_reactivated") as string
      });

      if (this.openInvoicePreviewModal) {
        this.$store.dispatch(`data/${DataModules.INVOICES}/openPreviewModal`, {
          invoiceId: _.get(this.invoice, "id")
        });
      }
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public get isSubmitDisabled() {
    if (this.isProcessing) return true;
    if (this.hasErrors) return true;
    return false;
  }
}
